import Camera from './Camera3D';
import Vector3 from '../../math/Vector3';

export default class OrthographicCamera extends Camera {
	height: number;

	constructor (height: number = 10, near?: number, far?: number) {
		super(near, far);

		this.height = height;

		this._setProjection();
	}

	getHeight () {
		return this.height;
	}

	_setProjection () {
		var top    = this.height / 2;
		var bottom = -top;

		var right = top;
		var left  = bottom;

		this._projectionMatrix.setOrthogonalProjection(left, right, bottom, top, this._near, this._far);
	}

	updateBounds (viewWidth: number) {
		var dx = this.height / viewWidth;
		var dy = this.height;
		var dz = this._far - this._near;

		var v0 = this.rightward.multiplicationScalar(dx * 0.5);
		var v1 = this.upward.multiplicationScalar(dy * 0.5);
		var v2 = this.forward.multiplicationScalar(dz * 0.5);

		var tx = Math.abs(v0.x) + Math.abs(v1.x) + Math.abs(v2.x);
		var ty = Math.abs(v0.y) + Math.abs(v1.y) + Math.abs(v2.y);
		var tz = Math.abs(v0.z) + Math.abs(v1.z) + Math.abs(v2.z);

		var x = this.position.x;
		var y = this.position.y;
		var z = this.position.z;

		this.bounds[0] = x - tx;
		this.bounds[1] = x + tx;
		this.bounds[2] = y - ty;
		this.bounds[3] = y + ty;
		this.bounds[4] = z - tz;
		this.bounds[5] = z + tz;

		return true;
	}

	encloseCameraFrustum (camera: Camera, direction: Vector3) {
		// Bounds of objects casting shadows

		// Note: limiting to object within view bounds
		const cameraBounds = camera.bounds;
		const cx0 = cameraBounds[0];
		const cx1 = cameraBounds[1];
		const cy0 = cameraBounds[2];
		const cy1 = cameraBounds[3];
		const cz0 = cameraBounds[4];
		const cz1 = cameraBounds[5];

		// let cx0 =  Infinity;
		// let cx1 = -Infinity;
		// let cy0 =  Infinity;
		// let cy1 = -Infinity;
		// let cz0 =  Infinity;
		// let cz1 = -Infinity;

		// const displayLists = [camera._opaqueObjects, camera._transparentObjects, camera._transluscentObjects];
		// for (let d = 0; d < displayLists.length; d += 1) {
		// 	const displayList = displayLists[d];
		// 	for (let objectRef = displayList.first; objectRef !== null; objectRef = objectRef.next) {
		// 		const objectInView = objectRef.object;
		// 		const object = objectInView.object;

		// 		if (!object.castShadow) {
		// 			continue;
		// 		}

		// 		const bounds = object.bounds;
		// 		const x0 = bounds[0];
		// 		const x1 = bounds[1];
		// 		const y0 = bounds[2];
		// 		const y1 = bounds[3];
		// 		const z0 = bounds[4];
		// 		const z1 = bounds[5];

		// 		if (x0 < cx0) { cx0 = x0; }
		// 		if (x1 > cx1) { cx1 = x1; }
		// 		if (y0 < cy0) { cy0 = y0; }
		// 		if (y1 > cy1) { cy1 = y1; }
		// 		if (z0 < cz0) { cz0 = z0; }
		// 		if (z1 > cz1) { cz1 = z1; }
		// 	}
		// }


		// if (Math.random() < 0.03) {
		// 	console.error('cameraBounds', cameraBounds)
		// }

		// TODO: optimize the calculations and bounding box
		// Deducing 8 points in light view coordinates from those 6 bounds
		const p1 = this._viewMatrix.multiplyVector3(new Vector3(cx0, cy0, cz0));
		const p2 = this._viewMatrix.multiplyVector3(new Vector3(cx0, cy0, cz1));
		const p3 = this._viewMatrix.multiplyVector3(new Vector3(cx0, cy1, cz0));
		const p4 = this._viewMatrix.multiplyVector3(new Vector3(cx0, cy1, cz1));
		const p5 = this._viewMatrix.multiplyVector3(new Vector3(cx1, cy0, cz0));
		const p6 = this._viewMatrix.multiplyVector3(new Vector3(cx1, cy0, cz1));
		const p7 = this._viewMatrix.multiplyVector3(new Vector3(cx1, cy1, cz0));
		const p8 = this._viewMatrix.multiplyVector3(new Vector3(cx1, cy1, cz1));

		const dx = Math.max(p1.x, p2.x, p3.x, p4.x, p5.x, p6.x, p7.x, p8.x) - Math.min(p1.x, p2.x, p3.x, p4.x, p5.x, p6.x, p7.x, p8.x);
		const dy = Math.max(p1.y, p2.y, p3.y, p4.y, p5.y, p6.y, p7.y, p8.y) - Math.min(p1.y, p2.y, p3.y, p4.y, p5.y, p6.y, p7.y, p8.y);
		const dz = Math.max(p1.z, p2.z, p3.z, p4.z, p5.z, p6.z, p7.z, p8.z) - Math.min(p1.z, p2.z, p3.z, p4.z, p5.z, p6.z, p7.z, p8.z);

		this._near = -dz * 0.5;
		this._far = dz * 0.5;

		this.height = Math.max(dx, dy);
		const h = this.height * 0.5;

		this._projectionMatrix.setOrthogonalProjection(-h, h, -h, h, this._near, this._far);

		// For better precision in the computation of the depth
		// The light position is set in the middle of the bounds
		this.position.x = (cx0 + cx1) * 0.5;
		this.position.y = (cy0 + cy1) * 0.5;
		this.position.z = (cz0 + cz1) * 0.5;

		const focusPoint = this.position.addition(direction);
		this.lookAt(focusPoint);
	}
}
